/**
 * Primary navigation config for the Marketer View shell (SYR-68).
 *
 * Flat 6-item primary menu ("The …" naming convention) + a collapsible Admin
 * group and Help in the footer. `path` is relative to the brand root
 * (`/marketer-view/:brandHash`); the Briefing is the index (empty path).
 */

import React from 'react';
import {
  FileTextOutlined,
  AppstoreOutlined,
  CommentOutlined,
  BarChartOutlined,
  BulbOutlined,
  RobotOutlined,
  CheckSquareOutlined,
  FormOutlined,
  UserOutlined,
  BankOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

export interface MarketerNavItem {
  key: string;
  /** Path relative to the brand root; '' = the Briefing (index route). */
  path: string;
  /** i18n message id for the label. */
  labelId: string;
  defaultLabel: string;
  icon: React.ReactNode;
}

export const PRIMARY_NAV: MarketerNavItem[] = [
  {
    key: 'briefing',
    path: '',
    labelId: 'marketerView.nav.briefing',
    defaultLabel: 'The Briefing',
    icon: React.createElement(FileTextOutlined),
  },
  {
    key: 'detail',
    path: 'detail',
    labelId: 'marketerView.nav.detail',
    defaultLabel: 'The Detail',
    icon: React.createElement(AppstoreOutlined),
  },
  {
    key: 'responses',
    path: 'responses',
    labelId: 'marketerView.nav.responses',
    defaultLabel: 'The Responses',
    icon: React.createElement(CommentOutlined),
  },
  {
    key: 'benchmarks',
    path: 'benchmarks',
    labelId: 'marketerView.nav.benchmarks',
    defaultLabel: 'The Benchmarks',
    icon: React.createElement(BarChartOutlined),
  },
  {
    key: 'model-beliefs',
    path: 'model-beliefs',
    labelId: 'marketerView.nav.modelBeliefs',
    defaultLabel: 'The Machines',
    icon: React.createElement(RobotOutlined),
  },
  {
    key: 'expert',
    path: 'expert',
    labelId: 'marketerView.nav.expert',
    defaultLabel: 'The Expert',
    icon: React.createElement(BulbOutlined),
  },
  {
    key: 'action-plan',
    path: 'action-plan',
    labelId: 'marketerView.nav.actionPlan',
    defaultLabel: 'The Action Plan',
    icon: React.createElement(CheckSquareOutlined),
  },
];

/** Footer Admin group — rendered INSIDE the marketer shell (same content as the
 *  dashboard pages, but staying in the new view). `path` is relative to the
 *  brand root, like PRIMARY_NAV. */
export interface MarketerAdminItem {
  key: string;
  /** Path relative to the brand root (`/marketer-view/:brandHash`). */
  path: string;
  labelId: string;
  defaultLabel: string;
  icon: React.ReactNode;
  /** Only shown to organisation admins. */
  adminOnly?: boolean;
}

export const ADMIN_NAV: MarketerAdminItem[] = [
  {
    key: 'surveys',
    path: 'admin/surveys',
    labelId: 'marketerView.nav.manageSurveys',
    defaultLabel: 'Manage Surveys',
    icon: React.createElement(FormOutlined),
    adminOnly: true,
  },
  {
    key: 'customers',
    path: 'admin/customers',
    labelId: 'marketerView.nav.customers',
    defaultLabel: 'Customers',
    icon: React.createElement(UserOutlined),
    adminOnly: true,
  },
  {
    key: 'organisation',
    path: 'admin/organisation',
    labelId: 'marketerView.nav.organisation',
    defaultLabel: 'Organisation',
    icon: React.createElement(BankOutlined),
  },
];

export const HELP_NAV: MarketerAdminItem = {
  key: 'help',
  path: 'help',
  labelId: 'marketerView.nav.help',
  defaultLabel: 'Help',
  icon: React.createElement(QuestionCircleOutlined),
};
